import React from "react";
import Helmet from "react-helmet";
import { Link } from "react-router-dom";
import Section from "../../components/shared-ui/Section/Section";
import PEDLayout from "./PEDLayout";
import PEDHeroHeader from "../../components/HeroHeader/PEDHeroHeader";

import "../../css/PED/TakingDoptelet.scss";

export default function TakingDoptelet() {
  return (
    <React.Fragment>
      <Helmet>
        <title>Helping Your Child Take DOPTELET® and DOPTELET Sprinkle (avatrombopag): What to Expect</title>
        <meta name="description" content="Learn how to help your child take DOPTELET® and DOPTELET Sprinkle (avatrombopag) for persistent or chronic ITP." />
      </Helmet>
      <PEDLayout pageClass="ped-taking-doptelet">
        <PEDHeroHeader
          extraClass="large-hero inner-bg home-hero"
          markup={
            <>
              <h1>
                TAKING DOPTELET (AVATROMBOPAG) OR DOPTELET SPRINKLE (AVATROMBOPAG): A
                Caregiver’s Guide
              </h1>
              <p>
                Doptelet and Doptelet Sprinkle are once-a-day oral medications
                that help raise your child’s platelet counts without
                compromising some of their personal preferences. That means no
                needles for treatment injections and less time spent for extra
                doctor visits. Plus, the type of food doesn’t change how treatment
                works. Doptelet and Doptelet Sprinkle are not used to make
                platelet counts normal.
              </p>
            </>
          }
          maxWidth="525"
          bgImage={[
            "/images/ped/hero/hero-taking-doptelet-desktop@2x.png",
            "/images/ped/hero/hero-taking-doptelet-desktop@2x.png",
          ]}
          bgImageMobile={[
            "/images/ped/hero/hero-taking-doptelet.png",
            "/images/ped/hero/hero-taking-doptelet@2x.png",
          ]}
        />

        <Section sectionClass="section-padding prepare-doptelet pb-3">
          <h2>How to prepare and administer treatment</h2>
          <h3>
            Doptelet or Doptelet Sprinkle can work with your child’s schedule
          </h3>
          <p>Doptelet is a 20-mg tablet that is taken with food. Doptelet Sprinkle is a 10-mg capsule containing oral granules that are mixed into, and taken with, soft food or liquid. Based on how your child's platelet count responds, their doctor will decide how much medicine they need to take and for how long. The doctor will also check your child's platelet count before, during, and for at least 4 weeks after stopping treatment. Doptelet Sprinkle oral granules that come in a bottle are not the same dosage form as Doptelet tablets that come in a blister card and cannot be directly substituted for each other.
          </p>
        </Section>

        <Section sectionClass="coming-soon-sprinkle pb-5">
          <div className="text-center">
            <h2>Coming soon! Doptelet Sprinkle</h2>
            <p className="text-blue">
              Oral granules that must be sprinkled on a soft food or liquid.
            </p>
          </div>

          <div className="content">
            <h3 className="mb-4">
              Here are the steps to prepare and give a dose of Doptelet
              Sprinkle:
            </h3>
            <ol>
              <li>
                Gather supplies: capsule(s), liquid or soft food, cup or spoon,
                and mixing utensil (eg, stir stick or small spoon); then wash
                your hands
              </li>
              <li>
                <p>
                  Put a small amount of soft food or liquid in a cup or spoon.
                  The following soft foods and liquids can be used:
                </p>

                <div className="d-md-flex mt-3">
                  <div className="col pl-0 pb-3 pb-md-0">
                    <p>
                      <strong>Soft foods</strong>
                    </p>
                    <ul>
                      <li>applesauce</li>
                      <li>strawberry jelly</li>
                      <li>yogurt (plain)</li>
                    </ul>
                  </div>
                  <div className="col pl-0 pl-md-3">
                    <p>
                      <strong>Liquids</strong>
                    </p>
                    <ul>
                      <li>milk (whole or skim)</li>
                      <li>orange juice</li>
                      <li>pediatric electrolyte solution (unflavored)</li>
                      <li>water</li>
                    </ul>
                  </div>
                </div>
              </li>
              <li>
                <p>
                  Carefully open the capsule (or 2 capsules if prescribed) by
                  pulling it apart
                </p>
                <ul className="mt-3">
                  <li>
                    Sprinkle the entire contents of the capsule onto the soft
                    food or liquid that you put in the spoon or cup. Make sure
                    that all of the granules from the capsule have been
                    completely emptied
                  </li>
                </ul>
              </li>
              <li>
                Mix or stir the granules into the soft food or liquid. The
                granules will <strong>not</strong> dissolve
              </li>
              <li>
                Give the mixture <strong>right away; do not</strong> save for
                later use
              </li>
              <li>
                Rinse the spoon or cup with additional soft food or liquid to
                make sure that the entire dose is given
              </li>
              <li>Throw the empty capsule shells away in the trash</li>
            </ol>
            <div className="cta">
              Do not swallow the capsule whole or the empty capsule shells. Do
              not chew or crush the granules.
            </div>
          </div>
        </Section>

        <Section sectionClass="pb-4 pb-md-5">
          <p className="mb-0">
            Doptelet and Doptelet Sprinkle may be affected by other drugs and
            may require a dose adjustment when coadministered with other drugs.
            Tell your child’s doctor about other prescription or nonprescription
            medications or dietary supplements. If your child is taking CYP2C9
            and CYP3A4 inhibitors such as certain antibiotics, antifungals, or
            antiepileptic drugs, their doctor will start them on 20 mg of
            Doptelet tablet or 10 mg of Doptelet Sprinkle 3 times a week. If
            they are taking CYP2C9 and CYP3A4 inducers such as antibiotics,
            antifungals, or antiepileptic drugs, they will start on 40 mg of
            Doptelet tablet or 20 mg of Doptelet Sprinkle once a day. Please see
            the full{" "}
            <a
              href="https://doptelet.com/themes/pdf/prescribing-information.pdf"
              target="_blank"
              rel="noreferrer noopener"
            >
              Prescribing Information
            </a>{" "}
            for more information on dose adjustments a doctor may make.
          </p>
        </Section>

        <Section sectionClass="starting-doptelet-cta">
          <div className="d-flex flex-column flex-md-row starting-doptelet-cta-card">
            <div className="starting-doptelet-cta-content">
              <h2>Is your child starting Doptelet or Doptelet Sprinkle?</h2>
              <p>
                Read more about what to expect when taking treatment for
                persistent or chronic ITP.
              </p>
              <Link
                to={{
                  pathname: "/pdf/PP-29112_Doptelet_US_Commercial_Digital-Caregiver-Brochure.pdf"
                }}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary download download-white"
              >
                <span>Download Brochure</span>
              </Link>
            </div>

            <div className="starting-doptelet-cta-icon">
              <img
                src="/images/ped/icons/icon-starting-doptelet@2x.png"
                alt=""
              />
            </div>
          </div>
        </Section>

        <Section sectionClass="section-padding take-doptelet blue-cta-card">
          <div className="take-doptelet-card blue-cta-card-content">
            <div className="d-flex flex-column flex-lg-row">
              <div>
                <h2>Explore resources</h2>
                <p>
                  Find information and resources on starting Doptelet or
                  Doptelet Sprinkle, financial assistance, FAQs, and more.
                </p>
              </div>
              <div>
                <a className="btn btn-primary btn-arrow" href="/pediatric-itp/support-and-resources/itp-caregiver-resources/">
                  <span>Resources</span>
                </a>
              </div>
            </div>
          </div>
        </Section>
      </PEDLayout>
    </React.Fragment>
  );
}
